
import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Truck, Undo2, Sparkles } from 'lucide-react';
import { Order, Lead } from './types';
import { Card, Badge } from './SharedComponents';

export const CalendarPage = ({ orders, leads = [], onSelectOrder, onSelectLead }: any) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState<string>(new Date().toISOString().split('T')[0]);

  const monthName = currentDate.toLocaleString('pt-BR', { month: 'long', year: 'numeric' });
  
  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const date = new Date(year, month, 1);
    const days = [];
    const firstDayIndex = date.getDay();
    for (let i = 0; i < firstDayIndex; i++) days.push(null);
    while (date.getMonth() === month) {
      days.push(new Date(date));
      date.setDate(date.getDate() + 1);
    }
    return days;
  }, [currentDate]);

  const changeMonth = (offset: number) => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1));
  };

  const selectedDayTasks = useMemo(() => {
    const deliveries = orders.filter((o: Order) => o.dataEvento === selectedDay);
    const pickups = orders.filter((o: Order) => o.diaRetirada === selectedDay);
    const reservations = leads.filter((l: Lead) => l.followUpDate === selectedDay);
    return { deliveries, pickups, reservations };
  }, [selectedDay, orders, leads]);

  return (
    <div className="flex flex-col xl:flex-row gap-6 md:gap-8 animate-in fade-in duration-500 pb-20 p-4 md:p-6">
      <div className="flex-1 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-center bg-white p-4 rounded-3xl border border-gray-100 shadow-sm gap-4">
          <div className="flex items-center gap-4 w-full sm:w-auto justify-between">
            <h2 className="text-lg md:text-xl font-black text-secondary uppercase tracking-tight capitalize italic">{monthName}</h2>
            <div className="flex border border-gray-100 rounded-xl overflow-hidden">
              <button onClick={() => changeMonth(-1)} className="p-2.5 hover:bg-gray-50 transition-colors border-r border-gray-100 text-gray-600"><ChevronLeft size={18} /></button>
              <button onClick={() => changeMonth(1)} className="p-2.5 hover:bg-gray-50 transition-colors text-gray-600"><ChevronRight size={18} /></button>
            </div>
          </div>
          <button onClick={() => { const today = new Date(); setCurrentDate(today); setSelectedDay(today.toISOString().split('T')[0]); }} className="w-full sm:w-auto px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-primary bg-primary/5 hover:bg-primary/10 rounded-xl transition-all">Ir para Hoje</button>
        </div>

        <div className="bg-white rounded-[32px] border border-gray-100 shadow-xl overflow-hidden">
          <div className="grid grid-cols-7 bg-gray-50/50 border-b border-gray-100">
            {['D', 'S', 'T', 'Q', 'Q', 'S', 'S'].map((d, idx) => (
              <div key={`${d}-${idx}`} className="py-4 text-center text-[10px] font-black text-gray-400 uppercase tracking-widest">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {daysInMonth.map((date, idx) => {
              if (!date) return <div key={`empty-${idx}`} className="h-24 md:h-36 bg-gray-50/20 border-b border-r border-gray-50"></div>;
              const dateStr = date.toISOString().split('T')[0];
              const isSelected = selectedDay === dateStr;
              const isToday = new Date().toISOString().split('T')[0] === dateStr;
              
              const dayDeliveries = orders.filter((o: Order) => o.dataEvento === dateStr);
              const dayPickups = orders.filter((o: Order) => o.diaRetirada === dateStr);
              const dayReservations = leads.filter((l: Lead) => l.followUpDate === dateStr);
              
              return (
                <div key={dateStr} onClick={() => setSelectedDay(dateStr)} className={`h-24 md:h-36 p-1.5 md:p-3 border-b border-r border-gray-50 cursor-pointer transition-all hover:bg-primary/[0.02] group relative overflow-hidden ${isSelected ? 'bg-primary/[0.03]' : 'bg-white'}`}>
                  <div className="flex justify-between items-start mb-1 md:mb-2">
                    <span className={`inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 text-[10px] md:text-xs font-black rounded-xl transition-all ${isToday ? 'bg-primary text-white shadow-lg' : isSelected ? 'bg-secondary text-white' : 'text-gray-400 group-hover:text-secondary'}`}>
                      {date.getDate()}
                    </span>
                  </div>
                  
                  <div className="flex flex-col gap-1 overflow-hidden h-[calc(100%-35px)]">
                    {/* RESERVAS - AZUL */}
                    {dayReservations.map((l: Lead) => (
                      <div key={`res-${l.id}`} 
                        onClick={(e) => { e.stopPropagation(); onSelectLead(l); }}
                        className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-1.5 py-0.5 rounded-md border border-blue-100 hover:bg-blue-100 transition-colors">
                         <div className="w-1 h-1 bg-blue-500 rounded-full shrink-0"></div>
                         <span className="text-[7px] md:text-[8px] font-black uppercase truncate leading-tight">{l.name}</span>
                      </div>
                    ))}
                    
                    {/* ENTREGAS - VERDE */}
                    {dayDeliveries.map((o: Order) => (
                      <div key={`del-${o.id}`} 
                        onClick={(e) => { e.stopPropagation(); onSelectOrder(o); }}
                        className="flex items-center gap-1.5 bg-green-50 text-green-700 px-1.5 py-0.5 rounded-md border border-green-100 hover:bg-green-100 transition-colors">
                         <div className="w-1 h-1 bg-green-500 rounded-full shrink-0"></div>
                         <span className="text-[7px] md:text-[8px] font-black uppercase truncate leading-tight">{o.nomeEvento}</span>
                      </div>
                    ))}

                    {/* RETIRADAS - ÂMBAR */}
                    {dayPickups.map((o: Order) => (
                      <div key={`pick-${o.id}`} 
                        onClick={(e) => { e.stopPropagation(); onSelectOrder(o); }}
                        className="flex items-center gap-1.5 bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded-md border border-amber-100 hover:bg-amber-100 transition-colors">
                         <div className="w-1 h-1 bg-amber-500 rounded-full shrink-0"></div>
                         <span className="text-[7px] md:text-[8px] font-black uppercase truncate leading-tight">{o.nomeEvento}</span>
                      </div>
                    ))}
                  </div>

                  {isSelected && <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-primary"></div>}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="w-full xl:w-96 shrink-0 space-y-6">
        <Card className="h-full flex flex-col min-h-[400px] border-none shadow-2xl bg-white rounded-[32px] overflow-hidden">
          <div className="p-6 md:p-8 border-b border-gray-100 bg-gray-50/50">
            <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">DETALHES DA DATA</h3>
            <p className="text-base md:text-lg font-black text-secondary uppercase italic">
              {new Date(selectedDay + 'T12:00:00').toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'long' })}
            </p>
          </div>
          
          <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6 scrollbar-hide">
            <section>
              <h4 className="text-[9px] font-black text-blue-500 uppercase flex items-center gap-2 mb-4">
                <Sparkles size={14} /> Reservas do Comercial (CRM)
              </h4>
              {selectedDayTasks.reservations.length > 0 ? (
                <div className="space-y-3">
                  {selectedDayTasks.reservations.map((l: Lead) => (
                    <DayItem key={l.id} title={l.name} subtitle={l.company} time="CRM" type="reservation" onClick={() => onSelectLead(l)} />
                  ))}
                </div>
              ) : <p className="text-[10px] text-gray-300 italic text-center py-4 border-2 border-dashed border-gray-50 rounded-2xl uppercase">Sem Reservas</p>}
            </section>

            <section>
              <h4 className="text-[9px] font-black text-green-600 uppercase flex items-center gap-2 mb-4">
                <Truck size={14} /> Entregas Confirmadas
              </h4>
              {selectedDayTasks.deliveries.length > 0 ? (
                <div className="space-y-3">
                  {selectedDayTasks.deliveries.map((o: Order) => (
                    <DayItem key={o.id} title={o.nomeEvento} subtitle={`REF: ${o.id}`} time={o.horarioEntrega} type="delivery" onClick={() => onSelectOrder(o)} />
                  ))}
                </div>
              ) : <p className="text-[10px] text-gray-300 italic text-center py-4 border-2 border-dashed border-gray-50 rounded-2xl uppercase">Livre</p>}
            </section>

            <section>
              <h4 className="text-[9px] font-black text-amber-500 uppercase flex items-center gap-2 mb-4">
                <Undo2 size={14} /> Retiradas Programadas
              </h4>
              {selectedDayTasks.pickups.length > 0 ? (
                <div className="space-y-3">
                  {selectedDayTasks.pickups.map((o: Order) => (
                    <DayItem key={o.id} title={o.nomeEvento} subtitle={`REF: ${o.id}`} time={o.horarioRetirada} type="pickup" onClick={() => onSelectOrder(o)} />
                  ))}
                </div>
              ) : <p className="text-[10px] text-gray-300 italic text-center py-4 border-2 border-dashed border-gray-50 rounded-2xl uppercase">Livre</p>}
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

const DayItem = ({ title, subtitle, time, type, onClick }: any) => {
  const styles = {
    delivery: 'border-l-green-500 bg-green-50/20',
    pickup: 'border-l-amber-500 bg-amber-50/20',
    reservation: 'border-l-blue-500 bg-blue-50/20'
  };

  const icons = {
    delivery: <Truck size={14} className="text-green-600" />,
    pickup: <Undo2 size={14} className="text-amber-600" />,
    reservation: <Sparkles size={14} className="text-blue-600" />
  };

  const labels = {
    delivery: 'SAÍDA / MONTAGEM',
    pickup: 'RETORNO / RETIRADA',
    reservation: 'RESERVA COMERCIAL'
  };

  return (
    <div onClick={onClick} className={`p-4 bg-white border border-gray-100 rounded-2xl hover:shadow-lg cursor-pointer transition-all border-l-4 ${styles[type as keyof typeof styles]}`}>
      <div className="flex justify-between items-start mb-2">
        <span className="text-[10px] font-black text-gray-400 flex items-center gap-1">
          {icons[type as keyof typeof icons]} {time}
        </span>
        <Badge className={`${type === 'delivery' ? 'bg-green-100 text-green-700' : type === 'pickup' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'} text-[7px] border-none px-1.5`}>
          {labels[type as keyof typeof labels]}
        </Badge>
      </div>
      <h5 className="text-xs font-black text-secondary uppercase truncate">{title}</h5>
      <p className="text-[9px] text-gray-400 font-bold uppercase mt-1">{subtitle}</p>
    </div>
  );
};
